import { remotes, branding, clinicTenantId } from './config';
import { validClinicSession } from './session-entitlements';
import { logout, switchClinicAccount, username } from './auth';
import { applyBranding, clinicName, featureOn, loadClinicAssignment, loadClinicBranding, logoUrl, productOn, tenantsMe, type TenantMe } from './entitlements';
import { ANAMNESIS_PROTOCOL } from './protocolo-anamnesis';
import { asHistoria, asReceta, emptyHistoria, emptyReceta, historiaHtml, recetaHtml } from './print-docs';
import { getEncounter, listEncounters, searchCie10, searchCie11, type EncounterView } from './clinic-api';
import { renderConsulta } from './consulta';

interface NfManifest {
  exposes: Array<{ key: string; outFileName: string }>;
}

interface FederatedElementModule {
  defineElement: () => Promise<string>;
}

export async function renderApp(root: HTMLElement): Promise<void> {
  const [me, publicBrand, assignment] = await Promise.all([tenantsMe(), loadClinicBranding(), loadClinicAssignment()]);
  if (!validClinicSession(me, clinicTenantId())) {
    const current = username();
    root.innerHTML = `<section class="boot access-denied">
      <h1>Esta cuenta no pertenece a ${escapeHtml(clinicName(publicBrand))}</h1>
      <p>${current ? `La sesión activa es <strong>${escapeHtml(current)}</strong>. ` : ''}Entra con una cuenta asignada a la Organization <code>${escapeHtml(clinicTenantId())}</code>.</p>
      <button class="btn primary" id="switch-account" type="button">Cambiar de cuenta</button>
      <a class="btn quiet" href="/">Volver al sitio</a>
    </section>`;
    root.querySelector('#switch-account')?.addEventListener('click', () => void switchClinicAccount());
    return;
  }
  if (me?.branding) applyBranding(me.branding);
  else applyBranding(publicBrand);
  const name = me?.branding?.displayName || me?.name || clinicName(publicBrand);
  const logo = logoUrl(!!(me?.branding?.hasLogo || publicBrand.hasLogo));
  const dictationOn = productOn(me, null, 'dictation');
  const documentsOn = productOn(me, null, 'documents');
  const vyendeloOn = productOn(me, assignment, 'vyendelo');
  const formatosOn = featureOn(me, 'formatos-msp', false);
  const odontogramaOn = featureOn(me, 'odontograma', false);
  const protocoloOn = featureOn(me, 'protocolo-home', false);
  const cieHomeOn = featureOn(me, 'cie-pack-local', false);
  const path = location.pathname.replace(/\/+$/, '') || '/';
  document.title = `${name} · consultorio`;

  root.innerHTML = `
    <header class="appbar flex items-center gap-4 bg-brand px-6 py-3 text-white">
      <a class="brand" href="/app">
        <img src="${logo}" alt="" />
        ${escapeHtml(name)}
      </a>
      <nav>
        <a class="${path === '/app' ? 'active' : ''}" href="/app">Inicio</a>
        ${dictationOn ? `<a class="${path.startsWith('/app/dictado') ? 'active' : ''}" href="/app/dictado">Dictado</a>` : ''}
        ${documentsOn ? `<a class="${path.startsWith('/app/documentos') ? 'active' : ''}" href="/app/documentos">Documentos</a>` : ''}
        ${vyendeloOn ? `<a class="${path.startsWith('/app/facturacion') ? 'active' : ''}" href="/app/facturacion">Facturación</a>` : ''}
        <a class="${path.startsWith('/app/consulta') ? 'active' : ''}" href="/app/consulta">Consulta</a>
        ${formatosOn ? `<a class="${path.startsWith('/app/formatos') ? 'active' : ''}" href="/app/formatos/receta">Formatos</a>` : ''}
      </nav>
      <span class="spacer"></span>
      <span class="who">${escapeHtml(username())}</span>
      <a class="btn ghost" href="/">Sitio</a>
      <button class="btn ghost" type="button" id="salir">Salir</button>
    </header>
    <div id="stage"></div>
  `;
  root.querySelector('#salir')?.addEventListener('click', () => void logout());
  const stage = root.querySelector('#stage') as HTMLElement;

  try {
    if (path === '/app' || path === '') {
    renderHome(stage, { name, dictationOn, documentsOn, vyendeloOn, me, odontogramaOn, protocoloOn, cieHomeOn, formatosOn });
      return;
    }
    if (path.startsWith('/app/consulta')) {
      const assigned = me?.specialties?.length ? me.specialties : [me?.specialty || 'odontologia'];
      const requested = new URLSearchParams(location.search).get('specialty') || '';
      const consultSpecialty = assigned.includes(requested) ? requested : me?.specialty || assigned[0] || 'odontologia';
      await renderConsulta(stage, name, (id) => {
        location.assign(`/app/formatos/receta?id=${encodeURIComponent(String(id))}`);
      }, consultSpecialty, assigned);
      return;
    }
    if (path.startsWith('/app/formatos/historia')) {
      if (!formatosOn) {
        stage.innerHTML = '<p class="boot">Formatos MSP no está asignado a esta clínica.</p>';
        return;
      }
      await renderFormato(stage, 'historia', name, logo);
      return;
    }
    if (path.startsWith('/app/formatos')) {
      if (!formatosOn) {
        stage.innerHTML = '<p class="boot">Formatos MSP no está asignado a esta clínica.</p>';
        return;
      }
      await renderFormato(stage, 'receta', name, logo);
      return;
    }
    if (path.startsWith('/app/documentos')) {
      if (!documentsOn) {
        stage.innerHTML = '<p class="boot">Documentos no está asignado a esta clínica. El master lo enciende en vyecom-admin.</p>';
        return;
      }
      await defineRemote(remotes.documents);
      stage.innerHTML = '<div class="workspace"><vyedocs-documents></vyedocs-documents></div>';
      return;
    }
    if (path.startsWith('/app/facturacion')) {
      if (!vyendeloOn) {
        stage.innerHTML = '<p class="boot">Vyendelo no está asignado a esta clínica.</p>';
        return;
      }
      await defineRemote(remotes.vyendelo);
      stage.innerHTML = '<div class="workspace"><vyendelo-invoicing></vyendelo-invoicing></div>';
      return;
    }
    if (path.startsWith('/app/dictado')) {
      if (!dictationOn) {
        stage.innerHTML = '<p class="boot">Dictado no está asignado a esta clínica.</p>';
        return;
      }
      await defineRemote(remotes.dictation);
      window.__VYEDOCS_CLINIC__ = { name, logo };
      const wrap = document.createElement('div');
      wrap.className = 'workspace';
      const el = document.createElement('vyedocs-dictation') as HTMLElement & {
        clinicName?: string;
        logoUrl?: string;
      };
      el.setAttribute('clinic-name', name);
      el.setAttribute('logo-url', logo);
      el.clinicName = name;
      el.logoUrl = logo;
      wrap.appendChild(el);
      stage.replaceChildren(wrap);
      return;
    }
      renderHome(stage, { name, dictationOn, documentsOn, vyendeloOn, me, odontogramaOn, protocoloOn, cieHomeOn, formatosOn });
  } catch (err) {
    console.error(err);
    stage.innerHTML = `
      <p class="boot">No se pudieron cargar los productos asignados.
      En prod, nginx proxea <code>/mf/</code> a VyeDocs y Vyendelo.</p>`;
  }
}

function renderHome(
  stage: HTMLElement,
  ctx: {
    name: string;
    dictationOn: boolean;
    documentsOn: boolean;
    vyendeloOn: boolean;
    me: TenantMe | null;
    odontogramaOn: boolean;
    protocoloOn: boolean;
    cieHomeOn: boolean;
    formatosOn: boolean;
  }
): void {
  const products = ctx.me?.products
    ? Object.entries(ctx.me.products)
        .filter(([, on]) => on)
        .map(([code]) => code)
        .join(', ')
    : 'según asignación del master';
  const assignedSpecialties = [...new Set((ctx.me?.specialties?.length
    ? ctx.me.specialties
    : [ctx.me?.specialty || '']).filter(Boolean))];
  const activeSpecialty = assignedSpecialties.includes(ctx.me?.specialty || '')
    ? ctx.me!.specialty
    : assignedSpecialties[0] || ctx.me?.specialty || '';
  const steps = ANAMNESIS_PROTOCOL.map(
    (step) => `<li><strong>${escapeHtml(step.say)}</strong><span>${escapeHtml(step.mapsTo)}</span></li>`
  ).join('');

  stage.innerHTML = `
    <div class="workspace">
      <div class="dash-head">
        <p class="eyebrow">Consultorio · ${escapeHtml(ctx.me?.specialty || 'odontologia')}</p>
        <h1>Hola${username() ? `, ${escapeHtml(username())}` : ''}</h1>
        <p>Productos activos: ${escapeHtml(products)}. La receta y la historia deben llevar el mismo CIE y la misma prescripción.</p>
      </div>
      <div class="mod-grid">
        ${modCard('/app/consulta', 'Registrar consulta', 'Dictado → receta AM 00098 e historia del pack asignado, con número secuencial y firma (no rúbrica).')}
        ${ctx.dictationOn ? modCard('/app/dictado', 'Dictado', 'Producto VyeDocs: el pack de Admin decide CIE e historia (033 o consulta 002).') : ''}
        ${ctx.documentsOn ? modCard('/app/documentos', 'Documentos', 'Historia y receta con la línea gráfica de la clínica.') : ''}
        ${ctx.vyendeloOn ? modCard('/app/facturacion', 'Facturación', 'Vyendelo · comprobantes SRI (ambiente de pruebas hasta que el master habilite producción).') : ''}
        ${ctx.formatosOn ? modCard('/app/formatos/receta', 'Formato receta', 'Plantilla AM 00098 lista para imprimir o para que VyeDocs la rellene.') : ''}
        ${ctx.formatosOn ? modCard('/app/formatos/historia', 'Formato historia', ctx.odontogramaOn ? 'HCU MSP 033 odontología: filiación, odontograma FDI, CIE PRE/DEF.' : 'Historia de consulta (HCU 002): sin odontograma.') : ''}
      </div>
      ${ctx.protocoloOn ? `<section class="card" style="margin-top:1.25rem">
        <p class="kicker">Protocolo de dictado</p>
        <h2 class="serif" style="margin:0 0 0.75rem;font-size:1.2rem">Qué decir para una receta completa</h2>
        <ol class="protocol">${steps}</ol>
      </section>` : ''}
      ${ctx.cieHomeOn ? `<section class="card cie-assist" style="margin-top:1rem" aria-labelledby="cie-home-title">
        <p class="kicker">Asistente de codificación</p>
        <h2 id="cie-home-title" class="serif" style="margin:0 0 0.5rem;font-size:1.2rem">Sugerencias CIE · ${escapeHtml(specialtyName(activeSpecialty))}</h2>
        <p class="muted">La búsqueda usa el catálogo de la especialidad elegida. Revisa cada candidato y confirma el diagnóstico en la atención antes de guardarlo.</p>
        ${assignedSpecialties.length > 1
          ? `<label for="cie-home-specialty">Especialidad de la atención</label><select id="cie-home-specialty">${assignedSpecialties.map((item) => `<option value="${escapeAttr(item)}" ${item === activeSpecialty ? 'selected' : ''}>${escapeHtml(specialtyName(item))}</option>`).join('')}</select>`
          : `<input id="cie-home-specialty" type="hidden" value="${escapeAttr(activeSpecialty)}"><p class="muted">${escapeHtml(specialtyName(activeSpecialty))} · especialidad asignada a tu clínica</p>`}
        <label for="cie-home-query">Diagnóstico o frase clínica</label>
        <div class="row2"><input id="cie-home-query" type="search" autocomplete="off" placeholder="Ej.: diabetes mellitus tipo 2" aria-describedby="cie-home-status"><a id="cie-home-consult" class="btn primary" href="/app/consulta?specialty=${encodeURIComponent(activeSpecialty)}">Abrir atención</a></div>
        <div id="cie-home-status" class="cie-home-results" aria-live="polite" role="status"></div>
        <small class="muted">Las sugerencias piloto son candidatas para revisión profesional; no sustituyen el criterio del médico.</small>
      </section>` : ''}
    </div>`;
  bindCieHome(stage, assignedSpecialties, activeSpecialty);
}

function specialtyName(code: string): string {
  const names: Record<string, string> = {
    endocrinologia: 'Endocrinología', endodoncia: 'Endodoncia', ginecologia: 'Ginecología',
    'medicina-general': 'Medicina general', obstetricia: 'Obstetricia', pediatria: 'Pediatría',
    psicologia: 'Psicología', 'terapia-fisica': 'Terapia física',
    'odontologia-general': 'Odontología general', odontologia: 'Odontología', ortodoncia: 'Ortodoncia',
  };
  return names[code] || code.replace(/-/g, ' ');
}

function bindCieHome(stage: HTMLElement, assigned: string[], fallback: string): void {
  const query = stage.querySelector<HTMLInputElement>('#cie-home-query');
  const specialty = stage.querySelector<HTMLSelectElement | HTMLInputElement>('#cie-home-specialty');
  const status = stage.querySelector<HTMLElement>('#cie-home-status');
  const consult = stage.querySelector<HTMLAnchorElement>('#cie-home-consult');
  const title = stage.querySelector<HTMLElement>('#cie-home-title');
  if (!query || !specialty || !status || !consult || !title) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let requestId = 0;
  const selected = () => assigned.includes(specialty.value) ? specialty.value : fallback;
  const update = () => {
    const code = selected();
    title.textContent = `Sugerencias CIE · ${specialtyName(code)}`;
    consult.href = `/app/consulta?specialty=${encodeURIComponent(code)}`;
    if (timer) clearTimeout(timer);
    const text = query.value.trim();
    const id = ++requestId;
    if (text.length < 3) {
      status.replaceChildren();
      status.textContent = text ? 'Escribe al menos 3 caracteres para buscar.' : '';
      return;
    }
    status.textContent = `Buscando en ${specialtyName(code)}…`;
    timer = setTimeout(async () => {
      try {
        const [cie10, cie11] = await Promise.all([searchCie10(text, code), searchCie11(text, code)]);
        if (id !== requestId || code !== selected() || text !== query.value.trim()) return;
        const cie10Rows = cie10.available
          ? cie10.results.filter((row) => matchesResultSpecialty(row.specialty, code))
          : [];
        const cie11Rows = cie11.available
          ? cie11.results.filter((row) => !row.specialty || matchesResultSpecialty(row.specialty, code))
          : [];
        const results = [
          ...cie10Rows.map((row) => ({ system: 'CIE-10', code: row.code, description: row.description })),
          ...cie11Rows.map((row) => ({ system: 'CIE-11', code: row.code, description: row.description })),
        ];
        status.replaceChildren();
        if (!results.length) {
          status.textContent = cie10.available || cie11.available
            ? 'Sin coincidencias para esta especialidad. Prueba otros términos o registra el diagnóstico manualmente.'
            : 'El catálogo de esta especialidad aún no está disponible en el entorno.';
          return;
        }
        const pilot = cie10.readiness?.cie10_training_only || cie11.readiness?.cie11_training_only;
        const note = document.createElement('p');
        note.className = 'muted';
        note.textContent = pilot ? 'Resultados piloto: cada código requiere confirmación del profesional.' : 'Resultados de búsqueda; confirma el código antes de incorporarlo.';
        status.append(note);
        for (const row of results) {
          const item = document.createElement('div');
          item.className = 'cie-home-result';
          const label = document.createElement('span');
          label.textContent = `${row.system} · ${row.code} · ${row.description}`;
          const use = document.createElement('a');
          use.className = 'btn quiet';
          use.textContent = 'Revisar en atención';
          use.href = `/app/consulta?specialty=${encodeURIComponent(code)}&cie=${encodeURIComponent(`${row.code} ${row.description}`)}`;
          item.append(label, use);
          status.append(item);
        }
      } catch {
        if (id === requestId) status.textContent = 'No se pudo consultar el catálogo. Reintenta o registra el diagnóstico manualmente.';
      }
    }, 250);
  };
  query.addEventListener('input', update);
  specialty.addEventListener('change', update);
}

function specialtyKey(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function matchesResultSpecialty(result: string, selected: string): boolean {
  const resultKey = specialtyKey(result);
  if (resultKey === 'compartido' || resultKey === 'global') return true;
  const dental = new Set(['odontologia', 'odontologiageneral', 'ortodoncia', 'endodoncia']);
  const normalizedResult = dental.has(resultKey) ? 'odontologia' : resultKey;
  const normalizedSelected = dental.has(specialtyKey(selected)) ? 'odontologia' : specialtyKey(selected);
  return normalizedResult === normalizedSelected;
}

function modCard(href: string, title: string, body: string): string {
  return `<a class="card mod flex min-h-[11.5rem] flex-col gap-2 rounded-2xl border border-line bg-paper p-5 text-inherit no-underline shadow-sm hover:border-brand-2" href="${href}"><p class="kicker">Módulo</p><h3 class="text-lg font-semibold">${escapeHtml(title)}</h3><p class="text-sm text-muted">${escapeHtml(body)}</p><span class="btn primary mt-auto self-start">Abrir</span></a>`;
}

async function renderFormato(stage: HTMLElement, kind: 'receta' | 'historia', name: string, logo: string): Promise<void> {
  const id = new URLSearchParams(location.search).get('id');
  let encounter: EncounterView | null = null;
  let loadError = '';
  if (id) {
    try {
      encounter = await getEncounter(id);
    } catch (err) {
      loadError = err instanceof Error ? err.message : String(err);
    }
  }
  const recent = await listEncounters().catch(() => ({ items: [] as EncounterView[] }));
  const rx = encounter ? asReceta(encounter.receta, name, branding.city) : emptyReceta(name, branding.city);
  const hc = encounter ? asHistoria(encounter.historia, name) : emptyHistoria(name);
  const sheet = kind === 'historia' ? historiaHtml(hc, logo) : recetaHtml(rx, logo);
  const list = recent.items
    .slice(0, 12)
    .map(
      (row) =>
        `<a class="quiet-link" href="/app/formatos/${kind}?id=${row.id}">${escapeHtml(row.seqReceta)} · ${escapeHtml(row.seqHistoria)}</a>`
    )
    .join(' · ') || 'Aún no hay consultas guardadas.';
  stage.innerHTML = `
    <div class="print-actions">
      <a class="btn quiet" href="/app">Volver</a>
      <a class="btn quiet" href="/app/consulta">Nueva consulta</a>
      <a class="btn quiet" href="/app/formatos/receta${id ? `?id=${encodeURIComponent(id)}` : ''}">Receta ACESS</a>
      <a class="btn quiet" href="/app/formatos/historia${id ? `?id=${encodeURIComponent(id)}` : ''}">Historia 033</a>
      <button class="btn primary" type="button" id="print">Imprimir / PDF</button>
    </div>
    ${loadError ? `<p class="err boot">${escapeHtml(loadError)}</p>` : ''}
    ${!encounter && !loadError ? '<p class="boot">Plantilla vacía. Registra una consulta para numerar receta e historia.</p>' : ''}
    <p class="boot">${list}</p>
    ${sheet}`;
  stage.querySelector('#print')?.addEventListener('click', () => window.print());
}

async function defineRemote(remoteEntryUrl: string): Promise<void> {
  const res = await fetch(remoteEntryUrl);
  if (!res.ok) {
    throw new Error(`${remoteEntryUrl} HTTP ${res.status}`);
  }
  const manifest = (await res.json()) as NfManifest;
  const exposed = manifest.exposes.find((row) => row.key === './component');
  if (!exposed) {
    throw new Error(`${remoteEntryUrl} no expone ./component`);
  }
  const base = remoteEntryUrl.replace(/[^/]+$/, '');
  const href = new URL(exposed.outFileName, window.location.origin + base).href;
  const mod = (await import(/* @vite-ignore */ href)) as FederatedElementModule;
  if (!mod.defineElement) {
    throw new Error(`${href} no exporta defineElement`);
  }
  await mod.defineElement();
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;');
}
